import {
  ErrorAuthenticatorAssuranceLevelNotSatisfied,
  ErrorGeneric,
} from "@ory/kratos-client";
import { AxiosError } from "axios";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { Dispatch, SetStateAction } from "react";

import { Flow } from "./use-flow";
import { i18n } from "@/i18n";

export function handleFlowError<S>(
  router: AppRouterInstance,
  flowType: "login" | "registration" | "settings" | "recovery" | "verification",
  resetFlow: Dispatch<SetStateAction<S | undefined>>,
) {
  return (err: AxiosError<ErrorGeneric>) => {
    if (!err.response || err.response?.status === 0) {
      // wtf?
      console.error(err);
    }
    const body = err.response?.data;

    // Kratos rejects password login until the current email is verified.
    if (body?.error?.id === "session_verified_address_required") {
      const continuation = (
        body.error.details as
          | {
              continue_with?: { action: string; flow?: { id: string } }[];
            }
          | undefined
      )?.continue_with?.find((item) => item.action === "show_verification_ui");
      const locale = window.location.pathname.split("/")[1];
      const query = continuation?.flow?.id
        ? `?flow=${encodeURIComponent(continuation.flow.id)}`
        : "";

      router.push(`/${locale}/account/verification${query}`);
      return;
    }

    switch (body?.error?.id) {
      case "session_inactive":
        router.push("/account/login?return_to=" + window.location.href);
        return;
      case "session_aal2_required": {
        const error = body as ErrorAuthenticatorAssuranceLevelNotSatisfied;
        if (error.redirect_browser_to) {
          const redirectTo = new URL(error.redirect_browser_to);
          if (flowType === "settings") {
            redirectTo.searchParams.set("return_to", window.location.href);
          }
          // 2FA is enabled and enforced, but user did not perform 2fa yet!
          window.location.href = redirectTo.toString();
          return;
        }
        router.push(
          "/account/login?aal=aal2&return_to=" + window.location.href,
        );
        return;
      }
      case "session_refresh_required": {
        // Re-authenticate in the current locale and resume the settings UI,
        // rather than returning to Kratos's settings API endpoint.
        const locale =
          i18n.locales.find((locale) =>
            window.location.pathname.startsWith(`/${locale}/`),
          ) ?? i18n.defaultLocale;
        const params = new URLSearchParams({
          refresh: "true",
          return_to: window.location.href,
        });
        router.push(`/${locale}/account/login?${params.toString()}`);
        return;
      }
      case "self_service_flow_return_to_forbidden":
        // The flow expired, let's request a new one.
        console.warn("The return_to address is not allowed.");
        resetFlow(undefined);
        router.push("/account/" + flowType);
        return;
      case "self_service_flow_expired":
        // The flow expired, let's request a new one.
        console.warn(
          "Your interaction expired, please fill out the form again.",
        );
        resetFlow(undefined);
        router.push("/account/" + flowType);
        return;
      case "security_csrf_violation":
        // A CSRF violation occurred. Best to just refresh the flow!
        console.warn(
          "A security violation was detected, please fill out the form again.",
        );
        resetFlow(undefined);
        router.push("/account/" + flowType);
        return;
      case "security_identity_mismatch":
        // The requested item was intended for someone else. Let's request a new flow...
        resetFlow(undefined);
        router.push("/account/" + flowType);
        return;
      case "browser_location_change_required": {
        // Ory Kratos asked us to point the user to this URL.
        if (
          "redirect_browser_to" in body &&
          typeof body.redirect_browser_to === "string" &&
          body.redirect_browser_to
        ) {
          window.location.href = body.redirect_browser_to;
          return;
        }
        break;
      }
    }

    switch (err.response?.status) {
      case 410:
        // The flow expired, let's request a new one.
        resetFlow(undefined);
        router.push("/" + flowType);
        return;
    }

    // We are not able to handle the error? Return it.
    return Promise.reject(err);
  };
}

export function handleValidationError<T extends Flow>(
  setFlow: Dispatch<SetStateAction<T | undefined>>,
) {
  return (err: AxiosError) => {
    switch (err.response?.status) {
      case 400:
        // Status code 400 implies the form validation had an error
        setFlow(err.response?.data as T);
        return;
    }

    throw err;
  };
}
